import React, { useState } from 'react';
import { ARENAS } from '../constants';

const G = '#3A6A2A';

// ─────────────────────────────────────────────────────────────────────────
// Onboarding — 4 steps that walk a new user from welcome to a populated app.
// (Was previously 5; "Add to Home Screen" was removed because forcing the
//  PWA install mid-onboarding caused users to re-authenticate and skip the
//  rest of onboarding. That prompt now lives as a dismissible banner on the
//  Today screen instead.)
// ─────────────────────────────────────────────────────────────────────────

export default function Onboarding({ onComplete }) {
  const [step, setStep] = useState(0);

  // Collected during steps 2 and 3; passed to onComplete on finish.
  const [intention, setIntention] = useState({ text: '', arena: '' });
  const [practice, setPractice] = useState({ name: '', frequency: '', why: '' });

  const TOTAL_STEPS = 4;
  const pct = ((step + 1) / TOTAL_STEPS) * 100;

  function next() {
    if (step < TOTAL_STEPS - 1) {
      setStep(s => s + 1);
      return;
    }
    finish();
  }

  // Skip button on Intention/Practice steps; users can always add later.
  function skip() {
    setStep(s => s + 1);
  }

  function finish() {
    const setup = {};

    if (intention.text && intention.text.trim()) {
      setup.intention = {
        id: Date.now(),
        goal: intention.text.trim(),
        intention: '',
        visualization: '',
        why: '',
        barriers: '',
        timeline: '',
        steps: [],
        mentors: [],
        type: 'short',
        arena: intention.arena || '',
        created: new Date().toISOString(),
      };
    }

    if (practice.name && practice.name.trim()) {
      setup.practice = {
        id: `custom_${Date.now()}`,
        name: practice.name.trim(),
        started: new Date().toISOString().slice(0, 10),
        when: '',
        frequency: practice.frequency || '',
        why: practice.why || '',
        intentionId: setup.intention ? setup.intention.id : '',
      };
    }

    localStorage.setItem('bhn_onboarded', 'true');
    localStorage.setItem('bhn_onboarded_date', new Date().toISOString());
    onComplete(setup);
  }

  // Primary button is disabled on input steps until a required field is filled.
  // Note: step indices shift after removing Home Screen — intention is now step 1, practice is step 2.
  const canContinue = (() => {
    if (step === 1) return intention.text.trim().length > 0;
    if (step === 2) return practice.name.trim().length > 0;
    return true;
  })();

  const buttonLabel = step === TOTAL_STEPS - 1 ? 'Enter your practice' : 'Continue';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#FFFFFF' }}>
      {/* Progress bar */}
      <div style={{ height: 2, background: '#F0EDE8', flexShrink: 0 }}>
        <div style={{ height: '100%', background: G, width: `${pct}%`, transition: 'width .4s' }} />
      </div>

      {/* Step counter + optional back */}
      <div style={{ padding: '18px 28px 0', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 11, letterSpacing: '.18em', color: '#C8C4BE', textTransform: 'uppercase', fontWeight: 600 }}>
          {step + 1} / {TOTAL_STEPS}
        </div>
        {step > 0 && (
          <button onClick={() => setStep(s => s - 1)} style={{ background: 'none', border: 'none', fontSize: 12, color: '#A09888', cursor: 'pointer', padding: 0 }}>
            ← Back
          </button>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, padding: '20px 28px 0', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

        {/* STEP 1 — Welcome */}
        {step === 0 && (
          <>
            <div style={{ fontSize: 38, fontWeight: 800, color: '#111', letterSpacing: '-1px', lineHeight: 1.1, marginBottom: 16, whiteSpace: 'pre-line' }}>{'Welcome to\nBe Here Now'}</div>
            <div style={{ fontSize: 17, color: '#A09888', marginBottom: 28, lineHeight: 1.6 }}>You're here. Let's begin.</div>
            <div style={{ fontSize: 16, color: '#333', lineHeight: 1.85, flex: 1, whiteSpace: 'pre-line' }}>
              {"This is a space for a grounded daily practice — not a productivity tracker.\n\nOver the next few moments we'll name one intention that's calling you, and choose one small practice to serve it.\n\nTake your time. Nothing here has to be perfect."}
            </div>
          </>
        )}

        {/* STEP 2 — Your first intention */}
        {step === 1 && (
          <>
            <div style={{ fontSize: 38, fontWeight: 800, color: '#111', letterSpacing: '-1px', lineHeight: 1.1, marginBottom: 16, whiteSpace: 'pre-line' }}>{'Your first\nintention'}</div>
            <div style={{ fontSize: 17, color: '#A09888', marginBottom: 20, lineHeight: 1.6 }}>What's calling you right now?</div>
            <div style={{ fontSize: 14, color: '#555', lineHeight: 1.7, marginBottom: 22 }}>
              Name one thing you're moving toward. Big or small. Short-term or long-term. You can edit it later.
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div className="input-label">Your intention</div>
              <textarea
                className="j-textarea"
                placeholder="e.g. Move my body every day — or — Feel calmer at work"
                value={intention.text}
                onChange={e => setIntention(p => ({ ...p, text: e.target.value }))}
                rows={3}
                style={{ width: '100%', marginBottom: 18 }}
                autoFocus
              />
              <div className="input-label">Which area of life does this serve? (optional)</div>
              <select
                value={intention.arena}
                onChange={e => setIntention(p => ({ ...p, arena: e.target.value }))}
                style={{ width: '100%', padding: '10px 0', border: 'none', borderBottom: '1px solid #E8E4DF', background: 'transparent', fontSize: 14, color: '#333', marginBottom: 20, fontFamily: 'inherit', cursor: 'pointer' }}
              >
                <option value="">Choose one or skip</option>
                {ARENAS.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
              <button onClick={skip} style={{ background: 'none', border: 'none', color: '#A09888', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', alignSelf: 'flex-start', padding: '4px 0' }}>
                Skip for now
              </button>
            </div>
          </>
        )}

        {/* STEP 3 — Your first practice */}
        {step === 2 && (
          <>
            <div style={{ fontSize: 38, fontWeight: 800, color: '#111', letterSpacing: '-1px', lineHeight: 1.1, marginBottom: 16, whiteSpace: 'pre-line' }}>{'One small\npractice'}</div>
            <div style={{ fontSize: 17, color: '#A09888', marginBottom: 20, lineHeight: 1.6 }}>
              {intention.text.trim() ? 'What practice would serve that intention?' : 'What daily practice will you start with?'}
            </div>
            {intention.text.trim() && (
              <div style={{ background: '#F8FCF6', border: '1px solid #E8F0E4', borderRadius: 10, padding: '12px 14px', marginBottom: 20 }}>
                <div style={{ fontSize: 10, letterSpacing: '.12em', color: '#3A6A2A', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Your intention</div>
                <div style={{ fontSize: 14, color: '#333', lineHeight: 1.5 }}>{intention.text}</div>
              </div>
            )}
            <div style={{ fontSize: 14, color: '#555', lineHeight: 1.7, marginBottom: 22 }}>
              Pick something small enough you'll actually do it tomorrow. You can always add more.
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div className="input-label">Practice name</div>
              <input
                className="form-input"
                placeholder="e.g. 5 minutes of stretching"
                value={practice.name}
                onChange={e => setPractice(p => ({ ...p, name: e.target.value }))}
                autoFocus
              />
              <div className="input-label">How often</div>
              <input
                className="form-input"
                placeholder="e.g. Every morning"
                value={practice.frequency}
                onChange={e => setPractice(p => ({ ...p, frequency: e.target.value }))}
              />
              <div className="input-label">Why are you starting this? (optional)</div>
              <input
                className="form-input"
                placeholder="Your reason — a few words"
                value={practice.why}
                onChange={e => setPractice(p => ({ ...p, why: e.target.value }))}
                style={{ marginBottom: 20 }}
              />
              <button onClick={skip} style={{ background: 'none', border: 'none', color: '#A09888', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', alignSelf: 'flex-start', padding: '4px 0' }}>
                Skip for now
              </button>
            </div>
          </>
        )}

        {/* STEP 4 — Welcome gift + ready */}
        {step === 3 && (
          <>
            <div style={{ fontSize: 38, fontWeight: 800, color: '#111', letterSpacing: '-1px', lineHeight: 1.1, marginBottom: 16, whiteSpace: 'pre-line' }}>{'Your welcome\ngift'}</div>
            <div style={{ fontSize: 17, color: '#A09888', marginBottom: 28, lineHeight: 1.6 }}>Thank you for joining Be Here Now.</div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ background: '#FAFAF8', borderRadius: 16, padding: '22px 22px', border: '1px solid #E8E4DF' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#A09888', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 10 }}>20% off your first order</div>
                <div style={{ fontSize: 30, fontWeight: 800, color: '#111', letterSpacing: '.1em', marginBottom: 8 }}>BEHERENOW20</div>
                <div style={{ fontSize: 14, color: '#777', lineHeight: 1.7, marginBottom: 14 }}>Use at beherenow.biz to shop our wellness collection.</div>
                <a href="https://beherenow.biz" target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: G, fontWeight: 600, textDecoration: 'none' }}>Shop now →</a>
              </div>
              <div style={{ fontSize: 15, color: '#555', lineHeight: 1.85 }}>
                Your practice is ready. When you enter, you'll find what you've set up already there — plus a library, a journal, and foundations for whenever you want to go deeper.
              </div>
            </div>
          </>
        )}

      </div>

      {/* Footer button */}
      <div style={{ padding: '16px 28px 36px', flexShrink: 0, borderTop: '1px solid #F5F3F0', background: '#FFFFFF' }}>
        <button
          onClick={next}
          disabled={!canContinue}
          style={{
            width: '100%', padding: '17px',
            background: canContinue ? G : '#D8D4CE', border: 'none', borderRadius: 12,
            color: 'white', fontSize: 15, fontWeight: 700,
            cursor: canContinue ? 'pointer' : 'not-allowed', letterSpacing: '.02em',
            transition: 'background .15s',
          }}
        >
          {buttonLabel}
        </button>
      </div>
    </div>
  );
}
